import React, { useState } from 'react';
import Icon from '@components/AppIcon';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import Select from '@components/ui/Select';

const BookingModal = ({ doctor, isOpen, onClose, onBookingConfirm }) => {
  const [formData, setFormData] = useState({
    patientName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    appointmentType: '',
    preferredDate: '',
    preferredTime: '',
    symptoms: '',
    previousTreatment: '',
    insurance: '',
    emergencyContact: '',
    emergencyPhone: ''
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !doctor) return null;

  const appointmentTypes = [
    { value: 'consultation', label: 'General Consultation' },
    { value: 'follow-up', label: 'Follow-up Visit' },
    { value: 'screening', label: 'Eye Screening' },
    { value: 'surgery-consult', label: 'Surgery Consultation' },
    { value: 'emergency', label: 'Emergency Consultation' }
  ];

  const timeSlots = [
    { value: '09:00', label: '9:00 AM' },
    { value: '10:00', label: '10:00 AM' },
    { value: '11:00', label: '11:00 AM' },
    { value: '14:00', label: '2:00 PM' },
    { value: '15:00', label: '3:00 PM' },
    { value: '16:00', label: '4:00 PM' }
  ];

  const insuranceOptions = [
    { value: 'nhif', label: 'NHIF' },
    { value: 'aap', label: 'AAR Insurance' },
    { value: 'jubilee', label: 'Jubilee Insurance' },
    { value: 'madison', label: 'Madison Insurance' },
    { value: 'private', label: 'Private Pay' },
    { value: 'other', label: 'Other Insurance' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    onBookingConfirm({
      doctor,
      appointment: formData,
      confirmationNumber: 'EYU' + Math.random().toString(36).substr(2, 9).toUpperCase()
    });
    
    setIsSubmitting(false);
    onClose();
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden medical-shadow">
        {/* Header */}
        <div className="bg-primary text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading font-bold text-xl mb-1">
                Book Appointment
              </h2>
              <p className="text-white/90 text-sm">
                with Dr. {doctor.name} • {doctor.specialty}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 medical-transition"
              aria-label="Close booking form"
            >
              <Icon name="X" size={16} color="white" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center space-x-4 mt-6">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep 
                    ? 'bg-white text-primary' :'bg-white/20 text-white/60'
                }`}>
                  {step}
                </div>
                {step < 3 && (
                  <div className={`w-8 h-0.5 mx-2 ${
                    step < currentStep ? 'bg-white' : 'bg-white/20'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6 max-h-96 overflow-y-auto">
          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
                Personal Information
              </h3>
              
              <Input
                label="Full Name"
                type="text"
                placeholder="Enter your full name"
                value={formData.patientName}
                onChange={(e) => handleInputChange('patientName', e.target.value)}
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />

                <Input
                  label="Phone Number"
                  type="tel"
                  placeholder="+254 700 000 000"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  required
                />
              </div>

              <Input
                label="Date of Birth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Emergency Contact Name"
                  type="text"
                  placeholder="Emergency contact name"
                  value={formData.emergencyContact}
                  onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
                />

                <Input
                  label="Emergency Contact Phone"
                  type="tel"
                  placeholder="+254 700 000 000"
                  value={formData.emergencyPhone}
                  onChange={(e) => handleInputChange('emergencyPhone', e.target.value)}
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
                Appointment Details
              </h3>

              <Select
                label="Appointment Type"
                options={appointmentTypes}
                value={formData.appointmentType}
                onChange={(value) => handleInputChange('appointmentType', value)}
                placeholder="Select appointment type"
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Preferred Date"
                  type="date"
                  min={getMinDate()}
                  value={formData.preferredDate}
                  onChange={(e) => handleInputChange('preferredDate', e.target.value)}
                  required
                />

                <Select
                  label="Preferred Time"
                  options={timeSlots}
                  value={formData.preferredTime}
                  onChange={(value) => handleInputChange('preferredTime', value)}
                  placeholder="Select time slot"
                  required
                />
              </div>

              <Select
                label="Insurance Provider"
                options={insuranceOptions}
                value={formData.insurance}
                onChange={(value) => handleInputChange('insurance', value)}
                placeholder="Select insurance provider"
                required
              />

              <div className="bg-muted p-4 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="Info" size={16} className="text-primary mt-0.5" />
                  <div className="text-sm text-text-secondary">
                    <p className="font-medium text-text-primary mb-1">Next Available Slot</p>
                    <p>{doctor.nextAvailable}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-4">
              <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
                Medical Information
              </h3>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Current Symptoms or Concerns
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  rows={4}
                  placeholder="Please describe your symptoms, concerns, or reason for visit..."
                  value={formData.symptoms}
                  onChange={(e) => handleInputChange('symptoms', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Previous Eye Treatment or Surgery
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  rows={3}
                  placeholder="Please list any previous eye treatments, surgeries, or medications..."
                  value={formData.previousTreatment}
                  onChange={(e) => handleInputChange('previousTreatment', e.target.value)}
                />
              </div>

              <div className="bg-accent/10 p-4 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="Shield" size={16} className="text-accent mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-text-primary mb-1">Privacy & Confidentiality</p>
                    <p className="text-text-secondary">
                      All information provided is confidential and protected under medical privacy laws.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="border-t p-6 bg-muted">
          <div className="flex items-center justify-between">
            <div className="text-text-secondary text-sm">
              Step {currentStep} of 3
            </div>
            <div className="flex space-x-3">
              {currentStep > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrevious}
                >
                  Previous
                </Button>
              )}
              {currentStep < 3 ? (
                <Button
                  type="button"
                  variant="default"
                  onClick={handleNext}
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="default"
                  loading={isSubmitting}
                  iconName="Calendar"
                  iconPosition="left"
                  iconSize={16}
                  onClick={handleSubmit}
                >
                  Confirm Appointment
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;